/*
  What every element asks for, whatever its kind: a name, a definition, the "needed by" line, and (if the person
  wants to choose it) a code. The server makes the code and checks that it is unique.
*/

import { useMemo, useState } from "react";

import { useApiQuery } from "../../../api/hooks";
import type { DeliverableKind, ElementRow, Id, NeededBy } from "../../../api/types";
import { NeededByPicker, type DeliverableChoice } from "../../items/attributes/NeededByPicker";
import { Check, TextArea, TextInput } from "../components/fields";
import type { Errors, KindSpec, Values } from "./types";

export type CommonValues = {
  name: string;
  definition: string;
  neededText: string;
  neededKind: DeliverableKind | "";
  /** The kinds of document that read it (their ids): the pick. */
  neededIds: Id[];
  notNamedYet: boolean;
  code: string;
};

/** `code`: a draft made from a printed line of a deliverable carries the code the sheet prints (Deliverables view, "+ Add field"). */
export function emptyCommon(
  draft?: { name?: string | null; definition?: string | null; needed_by_id?: string | null; deliverable_kind?: DeliverableKind | null; code?: string | null } | null,
): CommonValues {
  const neededId = draft?.needed_by_id ?? null;
  return {
    name: draft?.name ?? "",
    definition: draft?.definition ?? "",
    neededText: "",
    neededKind: neededId ? (draft?.deliverable_kind ?? "") : "",
    neededIds: neededId ? [neededId] : [],
    notNamedYet: false,
    code: draft?.code ?? "",
  };
}

export function commonFromElement(row: ElementRow): CommonValues {
  const needed = row.needed_by;
  return {
    name: row.name,
    definition: row.definition,
    neededText: needed?.not_named_yet ? "" : (needed?.text ?? ""),
    neededKind: needed?.deliverable_kind ?? "",
    neededIds: needed?.deliverable_ids ?? [],
    notNamedYet: needed?.not_named_yet ?? false,
    code: "",
  };
}

/** The "needed by" line to send, or null when the person said nothing about it. */
export function neededByOf(v: CommonValues): NeededBy | null {
  if (v.notNamedYet) return { text: "", deliverable_kind: null, not_named_yet: true };
  const text = v.neededText.trim();
  if (!text && v.neededIds.length === 0) return null;
  return { text, deliverable_kind: v.neededKind || null, not_named_yet: false, ...(v.neededIds.length > 0 ? { deliverable_ids: v.neededIds } : {}) };
}

export function validateCommon(v: CommonValues, spec: Pick<KindSpec, "autoText" | "nameLabel">, values: Values): Errors {
  const errors: Errors = {};
  const auto = spec.autoText?.(values) ?? null;
  if (!v.name.trim() && !auto) errors.name = "Give it a name.";
  if (!v.definition.trim() && !auto) errors.definition = "Write a definition: one or two plain sentences.";
  return errors;
}

/** The name and definition to send: what the person wrote, or what the spec makes from the answers. */
export function textToSend(v: CommonValues, spec: Pick<KindSpec, "autoText">, values: Values): { name: string; definition: string } {
  const auto = spec.autoText?.(values) ?? null;
  return { name: v.name.trim() || auto?.name || "", definition: v.definition.trim() || auto?.definition || "" };
}

interface Props {
  spec: Pick<KindSpec, "nameLabel" | "nameHint" | "definitionHint" | "autoText">;
  values: CommonValues;
  onChange: (patch: Partial<CommonValues>) => void;
  errors: Errors;
  /** The code box is for a new element only: a code is corrected on its own later. */
  showCode: boolean;
}

export function NameFields({ spec, values, onChange, errors, showCode }: Props) {
  const optional = !!spec.autoText;
  // A code that came with the draft is shown at once; the box is folded otherwise.
  const [codeOpen] = useState(() => values.code !== "");
  return (
    <>
      <TextInput label={spec.nameLabel} required={!optional} hint={spec.nameHint} value={values.name} error={errors.name} onChange={(name) => onChange({ name })} />
      <TextArea
        label="Definition"
        required={!optional}
        hint={spec.definitionHint ?? "One or two plain sentences."}
        rows={3}
        value={values.definition}
        error={errors.definition}
        onChange={(definition) => onChange({ definition })}
      />
      {showCode && (
        <details className="code-choice" open={codeOpen}>
          <summary>Choose the code myself</summary>
          <TextInput
            label="Code"
            hint="Leave empty and the app makes the code from the name. It tells you if the code is already used."
            value={values.code}
            onChange={(code) => onChange({ code })}
          />
        </details>
      )}
    </>
  );
}

/** Every kind of document of the workspace, for the pages that have no item to take the deliverables from. */
function useDocumentKindChoices(): DeliverableChoice[] {
  const kinds = useApiQuery("list_elements", { kind: "document_kind", limit: 200 }, { staleTime: 60_000 });
  return useMemo(() => (kinds.data?.items ?? []).map((row) => ({ id: row.id, name: row.name })), [kinds.data]);
}

/**
 * "Needed by" as a pick of deliverables (chips with a "+"). Free text written before the pick existed stays on the element and is
 * shown as a note until a deliverable is picked. `choices` left out, the pick offers every kind of document of the workspace.
 */
export function NeededByFields({ values, onChange, choices }: Pick<Props, "values" | "onChange"> & { choices?: readonly DeliverableChoice[] }) {
  const all = useDocumentKindChoices();
  return (
    <fieldset className="field field--group">
      <legend className="field__label">Needed by</legend>
      <p className="field__hint muted">The deliverables that read this. What nothing reads is left out.</p>
      <NeededByPicker choices={choices ?? all} known={all} value={values.neededIds} legacyText={values.neededText} onChange={(neededIds) => onChange({ neededIds, notNamedYet: false })} />
      <Check label="I cannot name one yet" checked={values.notNamedYet} onChange={(notNamedYet) => onChange({ notNamedYet })} />
    </fieldset>
  );
}
